import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import SosCitizenInfo from "./components/sos-citizen-info.jsx";
import {useQuery} from "@tanstack/react-query";
import sosService from "../../../../../services/web/sos-service.js";
import {useParams} from "react-router";
import {Card} from "../../../../../components/card/card.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import SosMap from "./components/sos-map.jsx";
import useSosSse from "./hooks/use-sos-sse.js";
import Chat from "../../../../../components/chat/chat.jsx";
import ChatHistory from "../../../../../components/chat/chat-history.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaCircleInfo} from "react-icons/fa6";
import {Modal} from "../../../../../components/modal/modal.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {FaPrint} from "react-icons/fa";
import SosPrint from "./components/sos-print.jsx";
import HeaderTemplateButton from "../../../../../components/button/header-template-button.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const SosView = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [citizen, setCitizen] = useState(undefined);
    const [apiData, setApiData] = useState(undefined);
    const [target, setTarget] = useState(undefined);

    const [finalizedModal, setFinalizedModal] = useState(false);
    const [finalizedData, setFinalizedData] = useState({providence: "", observation: ""});
    const [infoModal, setInfoModal] = useState(false);
    const [headerTemplateSelected, setHeaderTemplateSelected] = useState(undefined);
    const [showRoute, setShowRoute] = useState(false);

    const mapRef = useRef(undefined);
    const chatRef = useRef(undefined);

    const [chat, setChat] = useState({})
    const printRef = useRef(undefined);

    const actionRef = useRef(undefined);

    const {data, isSuccess, isLoading} = useQuery({
        queryFn: () => sosService.get(id),
        queryKey: ["event"]
    });

    const {data: providences} = useQuery({
        queryFn: () => sosService.providences().then(({data}) => data.map(({id, text}) => ({value: id, label: text}))),
        queryKey: ["providences"]
    });

    const {event, location, aggressorLocation} = useSosSse(data, isSuccess, id, chatRef, setTarget);

    const eventPath = Array.isArray(location?.eventLocation) ? location.eventLocation : [];

    const routePoints = eventPath.length
        ? eventPath
        : (location?.latitude != null && location?.longitude != null
            ? [{latitude: location.latitude, longitude: location.longitude}]
            : []);

    const initialPoint = routePoints[0];
    const finalPoint = routePoints[routePoints.length - 1];

    const hasRoute = routePoints.length > 1
        && (initialPoint.latitude !== finalPoint.latitude || initialPoint.longitude !== finalPoint.longitude);

    const mapLocation = finalPoint
        ? {...location, latitude: finalPoint.latitude, longitude: finalPoint.longitude}
        : location;

    useEffect(() => {
        if (!isSuccess) return;

        setCitizen(data.citizen);
        setApiData(data.apiData);
        setTarget(data.target);
    }, [isSuccess]);

    const isApi = citizen?.isApi;
    const defaultHeight = "h-[calc(100vh-197px)]";

    const ackButton = () => {
        projectUtils.swalMessage(
            "warning",
            "Assumir",
            "Tem certeza que deseja assumir o SOS?",
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) ackEvent(true);
            }
        )
    }

    const ackEvent = useCallback((showAlreadyAcknowledgedDialog = false) => {
        if (event.dtAck) {
            return;
        }

        sosService.ackEvent(id)
            .catch(error => {
                if (error.response.data) {
                    if (error.response.data.message === 'event.already.acknowledged') {
                        if (showAlreadyAcknowledgedDialog) {
                            setTimeout(() => {
                                projectUtils.swalErrorMessage('', 'O SOS já foi assumido', 'Ok', false, '', () => { });
                            }, 100)
                        }

                        return;
                    }
                }

                console.error(error);

                setTimeout(() => {
                    projectUtils.swalErrorMessage('', 'Falha ao assumir', 'Ok');
                }, 100)
            })
    }, [event]);

    const finalizedButton = () => {
        if (!finalizedData.observation || !finalizedData.providence) {
            projectUtils.swalErrorMessage('', 'Preencha os campos obrigatórios', 'Ok');
            return
        }

        const formData = new FormData();
        formData.append('observation', finalizedData.observation);
        formData.append('providence', finalizedData.providence);

        sosService.finalizeEvent(id, formData)
            .then(() => {
                setFinalizedModal(false);
            })
            .catch(error => {
                if (error.response.data) {
                    if (error.response.data.message === 'event.already.finalized') {
                        setTimeout(() => {
                            projectUtils.swalErrorMessage('', 'O SOS já foi finalizado', 'Ok', false, '', () => { });
                        }, 100)
                        return;
                    }
                }

                console.error(error);
                projectUtils.swalErrorMessage('', 'Falha ao finalizar evento', 'Ok');
            })
    }

    const getTitle = () => {
        if (isApi) {
            return `SOS ${apiData?.identifier} (via integração ${data?.userApi})`
        }

        const protectionProgramNames = citizen?.protectionPrograms?.map(i => i.name).join(", ");
        return `SOS ${protectionProgramNames ?? ""}`;
    };

    return (
        <>
            <Template className={"print:hidden"} isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        {getTitle()}
                    </Template.Title>
                    <div className={"flex items-center gap-2"}>
                        {
                            data?.citizen?.credential && !data.citizen.credential.isCitizen ?
                            <p>Acionado por <b>{data.citizen.credential.identification}</b> em <b>{data?.dtStart}</b></p> :
                            <p>Acionado em <b>{data?.dtStart}</b></p>
                        }

                        {
                            event?.dtEnd &&
                            <>
                                <Button title="Imprimir" onClick={() => printRef.current.print()}>
                                    <FaPrint />
                                </Button>

                                <HeaderTemplateButton
                                    selected={headerTemplateSelected}
                                    setSelected={setHeaderTemplateSelected}
                                />
                            </>
                        }
                    </div>
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body className={"flex gap-2 items-center "}>
                            <h2>
                                {citizen?.name} {citizen?.phone ? projectUtils.formatPhone(citizen?.phone) : "Protegido"}
                            </h2>

                            {
                                !event?.dtEnd &&
                                <Button target={"default"} onClick={() => navigate(`/gis?item_id=sos-${id}`, true)} title={"Abrir no mapa em tempo real"}>
                                    <img src={getAssetPath("img/gis-icon.svg")} alt=""/>
                                </Button>
                            }
                        </Card.Body>
                    </Card>

                    <div className={"grid grid-cols-12 gap-2 print:hidden"}>
                        <div className={"col-span-3"}>
                            <SosCitizenInfo
                                event={event}
                                location={location}
                                citizen={citizen}
                                apiData={apiData}
                                target={target}
                                isApi={isApi}
                                defaultHeight={defaultHeight}
                                actionRef={actionRef}
                                hasRoute={hasRoute}
                                showRoute={showRoute}
                                setShowRoute={setShowRoute}
                            />
                        </div>

                        <div className={isApi ? "col-span-9" : "col-span-6"}>
                            <SosMap
                                event={event}
                                location={mapLocation}
                                routePoints={routePoints}
                                initialPoint={initialPoint}
                                showRoute={hasRoute && showRoute}
                                aggressorLocation={aggressorLocation}
                                citizen={citizen}
                                target={target}
                                defaultHeight={defaultHeight}
                                actionRef={actionRef}
                                ref={mapRef}
                            />
                        </div>

                        {
                            !isApi &&
                            <div className="col-span-3">
                                {
                                    (isSuccess && event && !event?.dtEnd) &&
                                        <Chat
                                            url={API_KEEPER + '/sos/event/getChatAuth/' + id}
                                            height={defaultHeight}
                                            onSendMessage={() => ackEvent()}
                                            ref={chatRef}
                                        />
                                }

                                {
                                    (isSuccess && event && event?.dtEnd) &&
                                    <ChatHistory
                                        url={API_KEEPER + '/sos/event/getChat/' + id}
                                        height={defaultHeight}
                                        dataCallback={setChat}
                                    />
                                }
                            </div>
                        }
                    </div>

                    <Card className={"!mb-0"}>
                        <Card.Body className={"w-full flex justify-center items-center gap-2 print:hidden"}>
                            {
                                !event?.dtAck && !event?.dtEnd &&
                                <>
                                    <Button target={"warning"} onClick={ackButton}>
                                        Assumir
                                    </Button>
                                </>
                            }
                            {
                                event?.dtAck && !event?.dtEnd &&
                                <>
                                    <p className={"text-[15px]"}>Assumido por <strong>{event.userAck}</strong> em <strong>{event.dtAck}</strong></p>
                                    <Button target={"danger"} onClick={() => setFinalizedModal(true)}>
                                        Finalizar
                                    </Button>
                                </>
                            }
                            {
                                event?.dtEnd &&
                                <>
                                    <p className={"text-[15px]"}>
                                        Assumido por <strong>{event.userAck}</strong> em <strong>{event.dtAck}</strong> / Finalizado por <strong>{event.userEnd}</strong> em <strong>{event.dtEnd}</strong>
                                    </p>
                                    <Button onClick={() => setInfoModal(true)}>
                                        <FaCircleInfo/>
                                    </Button>
                                </>
                            }
                        </Card.Body>
                    </Card>

                </Template.Body>
            </Template>

            <SosPrint
                citizen={citizen}
                location={location}
                event={event}
                chat={chat}
                printRef={printRef}
                mapRef={mapRef}
                target={target}
                headerTemplateSelected={headerTemplateSelected}
                isApi={isApi}
                showRoute={hasRoute && showRoute}
            />

            <Modal open={finalizedModal} setOpen={setFinalizedModal} title={"Finalizar SOS"} overflow={true} className={"w-8/12"}>
                <Modal.Body className={"space-y-4"}>
                    <SelectComponent
                        value={finalizedData.providence}
                        options={providences ?? []}
                        label={"Providência *"}
                        onChange={(selected) => setFinalizedData(prev => ({...prev, providence: selected?.value}))}
                    />

                    <InputTextarea
                        id={"observation"}
                        className={"col-span-full"}
                        textAreaClass={"h-20"}
                        label={"Observação (Esta mensagem não será visível para o protegido) *"}
                        value={finalizedData.observation}
                        onChange={(e) => setFinalizedData(prev => ({...prev, observation: e.target.value}))}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button target="primary" onClick={finalizedButton} actionFeedbackType="save">
                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal open={infoModal} setOpen={setInfoModal} title={"SOS - finalizado"} overflow={true} className={"w-8/12"}>
                <Modal.Body className={"grid grid-cols-12 gap-3"}>
                    <InputForm
                        id={"dtEnd"}
                        label={"Data/hora"}
                        classNameWrapper={"col-span-3"}
                        value={event?.dtEnd}
                        disabled
                    />

                    <InputForm
                        id={"userAck"}
                        label={"Usuário"}
                        classNameWrapper={"col-span-9"}
                        value={event?.userAck}
                        disabled
                    />

                    <InputForm
                        id={"providence"}
                        label={"Providência"}
                        classNameWrapper={"col-span-12"}
                        value={event?.providence}
                        disabled
                    />

                    <InputTextarea
                        id={"observation"}
                        className={"col-span-12"}
                        textAreaClass={"h-20"}
                        label={"Observação"}
                        value={event?.observation}
                        disabled
                        onChange={(e) => setFinalizedData(prev => ({...prev, observation: e.target.value}))}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SosView;